import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShoppingBag, Layers, TrendingUp, Check, AlertTriangle } from "lucide-react";
import { fetchStack, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";

export default function BundleDetail() {
  const { slug } = useParams();
  const { addItem, setOpen } = useCart();
  const [bundle, setBundle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setBundle(null);
    setNotFound(false);
    fetchStack(slug).then(setBundle).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-display text-3xl">Bundle not found</p>
        <Link to="/build" className="text-accent font-mono-tech uppercase text-sm tracking-widest">
          ← Back to stacks
        </Link>
      </div>
    );

  if (!bundle) return <div className="min-h-screen pt-28" />;

  const addBundle = () => {
    bundle.products.forEach((p) =>
      addItem({ handle: p.handle, name: p.name, dose: p.dose, price: p.price, images: [p.image] }, 1)
    );
    toast.success(`${bundle.name} added — ${bundle.discount_percent}% saving applied at checkout`);
    setOpen(true);
  };

  return (
    <div className="pt-24 md:pt-28 pb-24">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8">
        <Link
          to="/build"
          className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-widest text-zinc-400 hover:text-accent transition-colors"
          data-testid="back-to-build"
        >
          <ArrowLeft size={14} /> Back to stacks
        </Link>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* image */}
        <div className="lg:sticky lg:top-28 self-start">
          <div className="relative aspect-[4/5] overflow-hidden clip-corner-lg border border-[#27272A]">
            <img src={bundle.image} alt={bundle.name} className="h-full w-full object-cover" data-testid="bundle-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 to-transparent" />
            <span className="absolute top-4 left-4 flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-widest text-white bg-ink-900/70 backdrop-blur px-3 py-1.5 border border-white/10">
              <Layers size={12} /> {bundle.purpose}
            </span>
            {bundle.discount_percent > 0 && (
              <span className="absolute top-4 right-4 flex items-center gap-1 bg-accent text-white font-mono-tech text-xs font-bold tracking-widest px-3 py-1.5">
                <TrendingUp size={12} /> SAVE {bundle.discount_percent}%
              </span>
            )}
          </div>
        </div>

        {/* info */}
        <div>
          <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">/ Bundle deal</span>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl leading-[0.9] mt-3">
            {bundle.name}
          </h1>
          <p className="text-zinc-300 text-lg leading-relaxed mt-5">{bundle.tagline}</p>

          <div className="flex items-baseline gap-4 mt-7">
            <span className="font-display font-black text-4xl" data-testid="bundle-total">{money(bundle.total)}</span>
            {bundle.discount_percent > 0 && (
              <span className="font-mono-tech text-lg text-zinc-600 line-through">{money(bundle.subtotal)}</span>
            )}
            <span className="font-mono-tech text-[11px] text-zinc-500 uppercase tracking-widest">
              AUD · save {money(bundle.subtotal - bundle.total)}
            </span>
          </div>

          {/* included products */}
          <div className="mt-8 space-y-3">
            <h3 className="font-mono-tech text-xs tracking-[0.3em] uppercase text-zinc-500">
              What's inside · {bundle.products.length} items
            </h3>
            {bundle.products.map((p) => (
              <Link
                key={p.handle}
                to={`/product/${p.handle}`}
                className="flex items-center gap-4 border border-[#27272A] bg-ink-800 p-3 hover:border-accent/60 transition-colors group"
                data-testid={`bundle-item-${p.handle}`}
              >
                <img src={p.image} alt={p.name} className="h-14 w-14 object-cover bg-ink-700 border border-[#27272A]" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold leading-none group-hover:text-accent transition-colors">
                    {p.name} <span className="text-zinc-500 font-mono-tech text-xs">· {p.dose}</span>
                  </p>
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-1">{p.summary}</p>
                </div>
                <span className="font-mono-tech text-sm text-zinc-400">{money(p.price)}</span>
                <ArrowRight size={15} className="text-zinc-600 group-hover:text-accent transition-colors" />
              </Link>
            ))}
          </div>

          <button
            onClick={addBundle}
            className="w-full mt-8 flex items-center justify-center gap-2 bg-accent hover:bg-accent-hover text-white font-display font-bold uppercase tracking-wide text-lg py-4 transition-colors clip-corner"
            data-testid="add-bundle-button"
          >
            <ShoppingBag size={18} /> Add Bundle · {money(bundle.total)}
          </button>

          <div className="flex items-center gap-2 mt-4 text-emerald-400">
            <Check size={15} />
            <span className="font-mono-tech text-[11px] uppercase tracking-widest">
              {bundle.discount_percent}% saving applied automatically at checkout
            </span>
          </div>

          <div className="border border-accent/40 bg-accent/5 p-4 clip-corner mt-6">
            <div className="flex gap-2 text-accent mb-2">
              <AlertTriangle size={15} />
              <span className="font-mono-tech text-[10px] uppercase tracking-widest">Research use only</span>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              For laboratory and research purposes only. Not for human consumption. Not a therapeutic
              good, food or supplement. You must be 18+ to purchase.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
